import { cn } from "@theprelude/core";
import { Button, UploadIcon } from "@theprelude/ds";
import { useRef, useState } from "react";

interface BaseProps {
  id?: string;
  accept?: string[];
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  className?: string;
}

interface SingleFileDropZoneProps extends BaseProps {
  multiple?: false;
  onChange: (file: File | null) => void;
}

interface MultipleFileDropZoneProps extends BaseProps {
  multiple: true;
  onChange: (files: FileList) => void;
}

type Props = SingleFileDropZoneProps | MultipleFileDropZoneProps;

export const FileDropZone = ({
  id,
  accept,
  multiple,
  disabled,
  loading,
  required,
  onChange,
  className,
}: Props) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (multiple) {
      onChange(files);
    } else {
      onChange(files.item(0));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files === null) {
      return;
    }
    if (multiple) {
      onChange(files);
    } else {
      onChange(files.item(0));
    }
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-4 p-6",
        isDragging && "bg-surface-selected border-primary-foreground",
        className,
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <UploadIcon className="text-secondary-foreground" />
      <p className="text-center">Drag and drop files here.</p>
      <p className="text-sm text-secondary-foreground">Limit 5MB</p>

      <input
        ref={fileInputRef}
        id={id}
        type="file"
        onChange={handleFileChange}
        multiple={multiple}
        required={required}
        className="hidden"
        accept={accept?.join(",")}
      />

      <Button
        intent="secondary"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        loading={loading}
      >
        {loading ? "Uploading..." : "Browse..."}
      </Button>
    </div>
  );
};
